import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../context/AuthContext';
import { useAppSettings } from '../../context/AppSettingsContext';

// ── Web Audio API — play notification sound ───────────────────────────────
function playNotifSound(type = 'default') {
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'message') {
      // Two-tone ding
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
    } else if (type === 'booking') {
      // Three ascending tones
      [660, 880, 1100].forEach((freq, i) => {
        const o2 = ctx.createOscillator();
        const g2 = ctx.createGain();
        o2.connect(g2); g2.connect(ctx.destination);
        o2.type = 'sine';
        o2.frequency.value = freq;
        g2.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.12);
        g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.2);
        o2.start(ctx.currentTime + i * 0.12);
        o2.stop(ctx.currentTime + i * 0.12 + 0.2);
      });
    } else {
      // Default soft ping
      osc.type = 'sine';
      osc.frequency.setValueAtTime(740, ctx.currentTime);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    }
  } catch {}
}

// ── Icon & color by notification type ────────────────────────────────────
const NOTIF_STYLE = {
  new_booking:           { icon: 'fa-calendar-check',   bg: '#2563EB', sound: 'booking'  },
  booking_cancelled:     { icon: 'fa-calendar-xmark',   bg: '#DC2626', sound: 'default'  },
  payment_released:      { icon: 'fa-indian-rupee-sign', bg: '#16A34A', sound: 'booking'  },
  new_review:            { icon: 'fa-star',              bg: '#D97706', sound: 'default'  },
  qr_scanned:            { icon: 'fa-qrcode',            bg: '#7C3AED', sound: 'default'  },
  slot_delay:            { icon: 'fa-clock',             bg: '#EA580C', sound: 'default'  },
  appointment_completed: { icon: 'fa-circle-check',      bg: '#0D9488', sound: 'default'  },
  new_message:           { icon: 'fa-comment-dots',      bg: '#0284C7', sound: 'message'  },
};

// ── Single toast card ─────────────────────────────────────────────────────
function ToastCard({ toast, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef(null);
  const settings = useAppSettings();

  useEffect(() => {
    // Slide in
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    // Auto-dismiss after 4.5s
    timerRef.current = setTimeout(() => dismiss(), 4500);
    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line
  }, []);

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setLeaving(true);
    setTimeout(() => onDismiss(toast.id), 320);
  }, [toast.id, onDismiss]);

  const style = NOTIF_STYLE[toast.type] || { icon: 'fa-bell', bg: '#0F172A', sound: 'default' };
  const logoSrc = settings.appLogo || '/logo.svg';
  const appName = settings.appName || 'Faristaa';

  return (
    <div
      onClick={toast.onClick ? () => { toast.onClick(); dismiss(); } : dismiss}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
        padding: '12px 14px',
        marginBottom: 8,
        cursor: 'pointer',
        borderLeft: `4px solid ${style.bg}`,
        minWidth: 280,
        maxWidth: 'calc(100vw - 32px)',
        transform: visible && !leaving ? 'translateY(0) scale(1)' : 'translateY(-110%) scale(0.96)',
        opacity: visible && !leaving ? 1 : 0,
        transition: 'transform 0.32s cubic-bezier(0.34,1.56,0.64,1), opacity 0.28s ease',
        willChange: 'transform, opacity',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        position: 'relative',
      }}>
      {/* App logo or icon */}
      <div style={{
        width: 38, height: 38, borderRadius: 10, background: style.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <img src={logoSrc} alt={appName}
          style={{ width: 22, height: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
          onError={e => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }} />
        <i className={`fa-solid ${style.icon}`}
          style={{ color: '#fff', fontSize: 16, display: 'none' }} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
          {toast.title || appName}
        </div>
        <div style={{ fontSize: 12, color: '#64748B', marginTop: 2, lineHeight: 1.4,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
          {toast.message}
        </div>
        <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 3 }}>just now</div>
      </div>

      {/* Close x */}
      <button
        onClick={e => { e.stopPropagation(); dismiss(); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer',
          color: '#94A3B8', fontSize: 14, padding: '4px 6px', flexShrink: 0 }}>
        ✕
      </button>
    </div>
  );
}

// ── Main exported component — mount once in App or layout ─────────────────
export default function RealtimeToast() {
  const { socket }            = useSocket();
  const { isLoggedIn, role }  = useAuth();
  const navigate              = useNavigate();
  const [toasts, setToasts]   = useState([]);
  const counterRef            = useRef(0);

  const addToast = useCallback((notif) => {
    const style = NOTIF_STYLE[notif.type] || { sound: 'default' };
    playNotifSound(style.sound);

    const id = ++counterRef.current;
    let onClick = null;

    // Navigate on tap
    if (notif.type === 'new_message' && notif.data?.conversationId) {
      const isStore = notif.data?.isStore || notif.title?.includes('Medicine Store');
      const convId  = notif.data.conversationId;
      if (isStore) {
        if (role === 'patient') {
          // Patient: /medicine-chat/:doctorId
          const safeDoctorId = notif.data?.doctorId ||
            (convId.match(/store_([0-9a-f]{24})/i) || [])[1];
          if (safeDoctorId) onClick = () => navigate(`/medicine-chat/${safeDoctorId}`);
        } else {
          // Doctor/Staff: /doctor/medicine-chat/:conversationId
          onClick = () => navigate(`/doctor/medicine-chat/${convId}`);
        }
      } else {
        const prefix = (role === 'doctor' || role === 'staff') ? '/doctor' : '';
        onClick = () => navigate(`${prefix}/chat/${convId}`);
      }
    } else if (notif.type === 'new_booking' || notif.type === 'appointment_completed') {
      onClick = () => navigate(role === 'patient' ? '/appointments' : '/doctor/appointments');
    } else if (notif.type === 'slot_delay') {
      onClick = () => navigate('/appointments');
    }

    setToasts(prev => [{ id, type: notif.type, title: notif.title, message: notif.message, onClick }, ...prev].slice(0, 5));
  }, [navigate, role]);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    if (!socket || !isLoggedIn) return;
    socket.on('new-notification', addToast);
    return () => socket.off('new-notification', addToast);
  }, [socket, isLoggedIn, addToast]);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center',
      pointerEvents: 'none', width: '100%', padding: '0 16px', boxSizing: 'border-box',
    }}>
      {toasts.map(t => (
        <div key={t.id} style={{ pointerEvents: 'auto', width: '100%', maxWidth: 400 }}>
          <ToastCard toast={t} onDismiss={dismissToast} />
        </div>
      ))}
    </div>
  );
}
